import { formatBytes } from "chalet-shared";
import * as I from "immutable";
import { createSelector } from "reselect";
import * as Uuid from "uuid";

import { Action, actionCreators } from "../actions";
import { UndoResetInteractions } from "../actions/rooms";
import { UsersSearchByIdsPayload, UsersSearchByNamePayload } from "../actions/roster";
import { humanizeDuration } from "../utils/moment";

import { getMaxUploadsSizeBytes } from "./config";

export type AppErrorInfo = {
    message: string;
    protoReconnectingMs?: number;
    unhideArguments?: UndoResetInteractions;
    cantClose?: boolean;
};

export type RoomErrorInfo = {
    neverClear?: boolean;
} & (
    | {
          type: "fileUploadTooBig";
          fileSize: number;
          fileName?: string;
      }
    | {
          type: "joinError";
          error: any;
      }
    | {
          type: "deleteTooLate";
          expiration?: number;
      }
    | {
          type: "performUsersSearchByNameError";
          originalPayload: UsersSearchByNamePayload;
      }
    | {
          type: "performUsersSearchByIdsError";
          originalPayload: UsersSearchByIdsPayload;
      }
    | {
          type: "ensureRosterForRoomError";
          originalPayload: string;
      }
    | {
          type: "sameroom";
      });

export type RoomError = {
    errorId: string;
    errorMessage: string;
    isWarning: boolean;
    canClose?: boolean;
    err: RoomErrorInfo;
};

type AppErrorTag =
    | "failed_to_unhide_the_room"
    | "failed_to_get_roster"
    | "oauth_state_failed"
    | "autoplay_disabled"
    | "lost_connection"
    | "not_connected"
    | "wrong_token"
    | "beta_login_failed"
    | "group_mark_read_error"
    | "notifications_muted";

export interface Store {
    appErrors: I.OrderedMap<string, AppErrorInfo & { errorId: string; tag?: AppErrorTag }>;
    roomErrors: I.OrderedMap<string, I.OrderedMap<string, RoomErrorInfo>>;
}

export function initialStore(): Store {
    return {
        appErrors: I.OrderedMap(),
        roomErrors: I.OrderedMap(),
    };
}

function addAppError(
    store: Store,
    info: AppErrorInfo,
    tag?: AppErrorTag,
    clearTags?: AppErrorTag[]
) {
    const errorId = Uuid.v1();
    const tags = clearTags ? clearTags : tag ? [tag] : undefined;
    const appErrors = tags ? clearAppErrorByTag(store, tags) : store.appErrors;
    return appErrors.set(errorId, { ...info, errorId, tag });
}

const hasAppTag = (tags: AppErrorTag[]) => (info: { tag?: AppErrorTag }) =>
    info.tag ? tags.includes(info.tag) : false;

function clearAppErrorByTag(store: Store, tags: AppErrorTag[]) {
    return store.appErrors.filterNot(hasAppTag(tags));
}

const connectionTags: AppErrorTag[] = ["lost_connection", "not_connected"];

export function updateAppErrors(store: Store, action: Action) {
    switch (action.type) {
        case actionCreators.startCallFailedToFindRoomId.type: {
            const { phoneNumber } = action.payload;
            return addAppError(store, {
                message: `${phoneNumber} is unreachable`,
            });
        }

        case actionCreators.createOAuthState.type: {
            return clearAppErrorByTag(store, ["oauth_state_failed"]);
        }
        case actionCreators.createOAuthStateFailed.type: {
            return addAppError(
                store,
                {
                    message: "Failed to get OAuth state",
                },
                "oauth_state_failed"
            );
        }

        case actionCreators.notificationSoundFailed.type: {
            return addAppError(
                store,
                {
                    message:
                        "Failed to play notification sound, please enable media autoplay for this website.",
                },
                "autoplay_disabled"
            );
        }

        case actionCreators.onOffline.type: {
            // start epic and check every second for timing
            if (action.payload.goingToReconnect) {
                return addAppError(
                    store,
                    {
                        message: "Internet connection lost. Reconnecting",
                        cantClose: true,
                    },
                    "lost_connection",
                    connectionTags
                );
            }
            return store.appErrors;
        }

        case actionCreators.onWrongToken.type: {
            return addAppError(
                store,
                {
                    message: "Your session has expired, please login again.",
                },
                "wrong_token",
                ["wrong_token", ...connectionTags]
            );
        }

        case actionCreators.setProtoToken.type: {
            if (action.payload.token) {
                return addAppError(
                    store,
                    {
                        message: "Connecting",
                    },
                    "not_connected",
                    ["wrong_token", ...connectionTags]
                );
            }
            return store.appErrors;
        }

        case actionCreators.onBetaLoginFailed.type: {
            const message = action.payload ? " (" + action.payload + ")" : "";
            return addAppError(
                store,
                { message: `Failed to sign in${message}, please try again` },
                "beta_login_failed"
            );
        }

        case actionCreators.onOnline.type: {
            return clearAppErrorByTag(store, connectionTags);
        }

        case actionCreators.updateProtoTimeBeforeReconnect.type: {
            const hasTag = hasAppTag(connectionTags);
            return store.appErrors.map(x => {
                if (hasTag(x)) {
                    return {
                        ...x,
                        protoReconnectingMs: action.payload.timeMs,
                    };
                }
                return x;
            });
        }

        case actionCreators.groupMarkAsReadError.type: {
            return addAppError(
                store,
                {
                    message: "Failed to mark all as read",
                },
                "group_mark_read_error"
            );
        }
        case actionCreators.groupMarkAsRead.type: {
            return clearAppErrorByTag(store, ["group_mark_read_error"]);
        }

        case actionCreators.getRoster.type: {
            return clearAppErrorByTag(store, ["failed_to_get_roster"]);
        }
        case actionCreators.getRosterFailed.type: {
            return addAppError(
                store,
                {
                    message: "Failed to get roster.",
                },
                "failed_to_get_roster"
            );
        }

        case actionCreators.undoResetInteractionsError.type: {
            const { retryArgs } = action.payload;
            return addAppError(
                store,
                {
                    message: `Failed to unhide ${
                        retryArgs.interactions.length === 1 ? "the room" : "rooms"
                    }.`,
                    unhideArguments: retryArgs,
                },
                "failed_to_unhide_the_room"
            );
        }

        case actionCreators.clearAppError.type: {
            return store.appErrors.remove(action.payload.errorId);
        }

        default: {
            return store.appErrors;
        }
    }
}

function clearRoomError(
    roomErrors: Store["roomErrors"],
    roomId: string,
    errType: RoomErrorInfo["type"]
) {
    return roomErrors.update(roomId, I.OrderedMap(), thisRoomErrors =>
        thisRoomErrors.filter(({ type }) => type !== errType)
    );
}

function addRoomError(roomErrors: Store["roomErrors"], roomId: string, error: RoomErrorInfo) {
    const errorId = Uuid.v1();
    return roomErrors.update(roomId, I.OrderedMap(), thisRoomErrors =>
        thisRoomErrors.filter(({ type }) => type !== error.type).set(errorId, error)
    );
}

export function updateRoomErrors(store: Store, action: Action) {
    const { roomErrors } = store;

    switch (action.type) {
        case actionCreators.clearRoomError.type: {
            const { roomId, errorId } = action.payload;
            const thisRoomErrors = roomErrors.get(roomId) || I.OrderedMap();
            const neverClearOnly = thisRoomErrors.filter(({ neverClear }) => neverClear);
            const newRoomErrors = errorId ? thisRoomErrors.delete(errorId) : neverClearOnly;
            return newRoomErrors.size
                ? roomErrors.set(roomId, newRoomErrors)
                : roomErrors.delete(roomId);
        }

        case actionCreators.uploadFile.type: {
            const { payload } = action;
            if (payload.status === "error") {
                if (
                    payload.err &&
                    payload.err.type === "file_upload_too_big" &&
                    payload.err.fileSize
                ) {
                    const { fileSize, fileName } = payload.err;
                    const { roomId } = payload.roomView;
                    const errorId = Uuid.v1();
                    const thisRoomErrors = roomErrors.get(roomId) || I.OrderedMap();

                    return roomErrors.set(
                        roomId,
                        thisRoomErrors.set(errorId, {
                            type: "fileUploadTooBig",
                            fileSize,
                            fileName,
                        })
                    );
                }
            }
            return roomErrors;
        }

        case actionCreators.onJoinError.type: {
            const { roomId, error } = action.payload;
            console.error("Failed to join", roomId, error);
            return addRoomError(roomErrors, roomId, {
                type: "joinError",
                error,
            });
        }

        case actionCreators.onDeleteTooLateError.type: {
            const { roomId, expiration } = action.payload;
            return addRoomError(roomErrors, roomId, {
                type: "deleteTooLate",
                expiration,
            });
        }

        case actionCreators.onRoomMeta.type: {
            const { room_id: roomId, meta } = action.payload;
            const errorId = "sameroomConnected";
            const thisRoomErrors = (roomErrors.get(roomId) || I.OrderedMap()).filter(
                ({ type }) => type !== "sameroom"
            );
            if (meta && "sameroom" in meta && !("private_hide_sameroom_warning" in meta)) {
                return roomErrors.set(
                    roomId,
                    thisRoomErrors.set(errorId, {
                        type: "sameroom",
                        neverClear: true,
                    })
                );
            } else {
                return roomErrors.set(roomId, thisRoomErrors.delete(errorId));
            }
        }

        case actionCreators.performUsersSearchByName.type: {
            const { roomView } = action.payload;
            return clearRoomError(roomErrors, roomView.roomId, "performUsersSearchByNameError");
        }
        case actionCreators.performUsersSearchByNameFailed.type: {
            const { roomView } = action.payload;
            return addRoomError(roomErrors, roomView.roomId, {
                type: "performUsersSearchByNameError",
                originalPayload: action.payload,
            });
        }

        case actionCreators.performUsersSearchByIds.type: {
            const { roomId } = action.payload.roomView;
            return clearRoomError(roomErrors, roomId, "performUsersSearchByIdsError");
        }
        case actionCreators.performUsersSearchByIdsFailed.type: {
            const { roomId } = action.payload.roomView;
            return addRoomError(roomErrors, roomId, {
                type: "performUsersSearchByIdsError",
                originalPayload: action.payload,
            });
        }

        case actionCreators.ensureRosterForRoom.type: {
            const roomId = action.payload;
            return clearRoomError(roomErrors, roomId, "ensureRosterForRoomError");
        }
        case actionCreators.ensureRosterForRoomFailed.type: {
            const roomId = action.payload;
            return addRoomError(roomErrors, roomId, {
                type: "ensureRosterForRoomError",
                originalPayload: action.payload,
            });
        }

        default: {
            return roomErrors;
        }
    }
}

export function update(store = initialStore(), action: Action): Store {
    if (actionCreators.onLogout.type === action.type) {
        return initialStore();
    }
    const { appErrors, roomErrors } = store;
    const newAppErrors = updateAppErrors(store, action);
    const newRoomErrors = updateRoomErrors(store, action);

    if (appErrors !== newAppErrors || roomErrors !== newRoomErrors) {
        return { ...store, appErrors: newAppErrors, roomErrors: newRoomErrors };
    } else {
        return store;
    }
}

export type OuterStore = { errors: Store };

function errorsToMessages(errors: I.OrderedMap<string, RoomErrorInfo>, maxFileUploadSize: number) {
    return errors.toArray().map(
        ([errorId, err]): RoomError => {
            let isWarning = false;
            let errorMessage = "UNKNOWN";
            let canClose = false;
            if (err.type === "fileUploadTooBig") {
                const size = err.fileSize - maxFileUploadSize;
                errorMessage = `${
                    err.fileName ? "File " + err.fileName : "This file"
                } is too big! It is ${formatBytes(
                    size
                )} more than the allowed maximum (${formatBytes(maxFileUploadSize)}).`;
                canClose = true;
            } else if (err.type === "deleteTooLate") {
                errorMessage = `This message cannot be deleted because it is ${
                    err.expiration ? "older than " + humanizeDuration(err.expiration) : "old"
                }.`;
                canClose = true;
            } else if (
                err.type === "performUsersSearchByNameError" ||
                err.type === "performUsersSearchByIdsError"
            ) {
                isWarning = true;
                canClose = true;
            } else if (err.type === "ensureRosterForRoomError") {
                canClose = true;
            }
            return {
                errorId,
                errorMessage,
                isWarning,
                canClose,
                err,
            };
        }
    );
}

export const selectErrorMessagesByRoomId = createSelector(
    (store: OuterStore) => store.errors.roomErrors,
    getMaxUploadsSizeBytes,
    (x, maxFileUploadSize) => x.map(x => errorsToMessages(x, maxFileUploadSize))
);

export function getAppErrors(store: OuterStore) {
    return store.errors.appErrors;
}

export const selectIsReconnecting = createSelector(
    (store: OuterStore) => store.errors.appErrors,
    appErrors => appErrors.some(hasAppTag(connectionTags))
);
